import { useEffect, useState, type ReactElement } from 'react'
import { getFigmaStatus, disconnectFigma, FigmaConnectionInfo, getJiraStatus, disconnectJira, disconnectJiraConnection, toggleJiraInstance, addJiraApiToken, testJiraApiToken, updateJiraApiToken, JiraConnectionInfo, JiraConnectionDetail, JiraInstanceInfo, getGoldenExamples, deleteGoldenExample, GoldenExample, type ThemePreference } from '../api'
import AppLayout from '../components/AppLayout'
import { useTheme } from '../contexts/ThemeContext'

const FIGMA_CLIENT_ID = import.meta.env.VITE_FIGMA_CLIENT_ID || ''
const FIGMA_REDIRECT_URI = `${window.location.origin}/settings/figma/callback`
const FIGMA_SCOPES = 'file_content:read,current_user:read'
const FIGMA_STATE_KEY = 'fd_qa_tools_figma_oauth_state'

const JIRA_CLIENT_ID = import.meta.env.VITE_JIRA_CLIENT_ID || ''
const JIRA_REDIRECT_URI = `${window.location.origin}/settings/jira/callback`
const JIRA_SCOPES = 'offline_access read:jira-work read:me read:board-scope:jira-software read:sprint:jira-software read:issue:jira-software read:issue-details:jira read:jql:jira read:project:jira'
const JIRA_STATE_KEY = 'fd_qa_tools_jira_oauth_state'

function FigmaIcon() {
  return (
    <svg width="24" height="36" viewBox="0 0 38 57" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38C23.2533 38 19 33.7467 19 28.5Z" fill="#1ABCFE"/>
      <path d="M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z" fill="#0ACF83"/>
      <path d="M19 0V19H28.5C33.7467 19 38 14.7467 38 9.5C38 4.25329 33.7467 0 28.5 0H19Z" fill="#FF7262"/>
      <path d="M0 9.5C0 14.7467 4.25329 19 9.5 19H19V0H9.5C4.25329 0 0 4.25329 0 9.5Z" fill="#F24E1E"/>
      <path d="M0 28.5C0 33.7467 4.25329 38 9.5 38H19V19H9.5C4.25329 19 0 23.2533 0 28.5Z" fill="#A259FF"/>
    </svg>
  )
}

function JiraIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.664 1H10.2603C10.2603 3.99443 12.7697 6.43176 15.8527 6.43176H18.147V8.55567C18.147 11.5501 20.6565 13.9875 23.7395 13.9875V2.04457C23.7395 1.45265 23.2735 1 22.664 1Z" fill="#2684FF"/>
      <path d="M16.5339 6.98877H4.13013C4.13013 9.98321 6.63956 12.4205 9.72258 12.4205H12.0169V14.5793C12.0169 17.5737 14.5264 20.0111 17.6094 20.0111V8.03329C17.6094 7.47623 17.1433 6.98877 16.5339 6.98877Z" fill="url(#jira-icon-grad-1)"/>
      <path d="M10.4038 13.0125H-1.99622e-05C-1.99622e-05 16.0069 2.50941 18.4442 5.59243 18.4442H7.88677V20.5682C7.88677 23.5626 10.3962 26 13.4792 26V14.057C13.4792 13.4651 12.9774 13.0125 12.4038 13.0125Z" fill="url(#jira-icon-grad-2)"/>
      <defs>
        <linearGradient id="jira-icon-grad-1" x1="17.3455" y1="7.01882" x2="12.2302" y2="12.45" gradientUnits="userSpaceOnUse">
          <stop offset="0.176" stopColor="#0052CC"/>
          <stop offset="1" stopColor="#2684FF"/>
        </linearGradient>
        <linearGradient id="jira-icon-grad-2" x1="11.5601" y1="13.0525" x2="5.63562" y2="18.9874" gradientUnits="userSpaceOnUse">
          <stop offset="0.176" stopColor="#0052CC"/>
          <stop offset="1" stopColor="#2684FF"/>
        </linearGradient>
      </defs>
    </svg>
  )
}

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: ReactElement }[] = [
  {
    value: 'light',
    label: 'Light',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
        <circle cx="8" cy="8" r="3" />
        <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    value: 'dark',
    label: 'Dark',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
        <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: 'auto',
    label: 'System',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
        <rect x="2" y="3" width="12" height="9" rx="1.5" />
        <path d="M6 14h4M8 12v2" strokeLinecap="round" />
      </svg>
    ),
  },
]

export default function Settings() {
  const { theme, setTheme } = useTheme()
  const [figmaStatus, setFigmaStatus] = useState<FigmaConnectionInfo | null>(null)
  const [jiraStatus, setJiraStatus] = useState<JiraConnectionInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [jiraLoading, setJiraLoading] = useState(true)
  const [disconnecting, setDisconnecting] = useState(false)
  const [jiraDisconnecting, setJiraDisconnecting] = useState(false)
  const [disconnectingConnectionId, setDisconnectingConnectionId] = useState<string | null>(null)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)
  const [showJiraDisconnectConfirm, setShowJiraDisconnectConfirm] = useState(false)
  const [jiraDisconnectConnectionId, setJiraDisconnectConnectionId] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [goldenExamples, setGoldenExamples] = useState<GoldenExample[]>([])
  const [goldenExamplesLoading, setGoldenExamplesLoading] = useState(true)
  const [deletingExampleId, setDeletingExampleId] = useState<string | null>(null)
  const [showApiTokenForm, setShowApiTokenForm] = useState(false)
  const [apiTokenUrl, setApiTokenUrl] = useState('')
  const [apiTokenEmail, setApiTokenEmail] = useState('')
  const [apiTokenValue, setApiTokenValue] = useState('')
  const [apiTokenSubmitting, setApiTokenSubmitting] = useState(false)
  const [apiTokenTesting, setApiTokenTesting] = useState(false)
  const [apiTokenTestResult, setApiTokenTestResult] = useState<{ success: boolean; userName?: string; error?: string } | null>(null)
  const [updatingTokenConnId, setUpdatingTokenConnId] = useState<string | null>(null)
  const [updateTokenValue, setUpdateTokenValue] = useState('')
  const [updateTokenSubmitting, setUpdateTokenSubmitting] = useState(false)

  const typeColors: Record<string, string> = {
    happy_path: 'bg-success/10 text-success border border-success/20',
    sad_path: 'bg-error/10 text-error border border-error/20',
    edge_case: 'bg-warning/10 text-warning border border-warning/20',
  }
  const typeLabels: Record<string, string> = {
    happy_path: 'Happy',
    sad_path: 'Sad',
    edge_case: 'Edge',
  }

  function showToast(message: string, type: 'success' | 'error' = 'success') {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => {
    // Check URL params for callback results
    const params = new URLSearchParams(window.location.search)
    const figmaParam = params.get('figma')
    const jiraParam = params.get('jira')

    if (figmaParam === 'connected') {
      showToast('Figma account connected successfully')
    } else if (figmaParam === 'error') {
      const message = params.get('message') || 'Failed to connect Figma account'
      showToast(message, 'error')
    }

    if (jiraParam === 'connected') {
      showToast('Jira account connected successfully')
    } else if (jiraParam === 'error') {
      const message = params.get('message') || 'Failed to connect Jira account'
      showToast(message, 'error')
    }

    // Clean URL params after showing toast
    if (figmaParam || jiraParam) {
      window.history.replaceState({}, '', '/settings')
    }

    loadFigmaStatus()
    loadJiraStatus()
  }, [])

  async function loadFigmaStatus() {
    setLoading(true)
    try {
      const status = await getFigmaStatus()
      setFigmaStatus(status)
    } catch {
      setFigmaStatus({ connected: false, status: 'disconnected' })
    } finally {
      setLoading(false)
    }
  }

  async function loadJiraStatus() {
    setJiraLoading(true)
    try {
      const status = await getJiraStatus()
      setJiraStatus(status)
    } catch {
      setJiraStatus({ connected: false, status: 'disconnected' })
    } finally {
      setJiraLoading(false)
    }
  }

  useEffect(() => {
    const idToken = localStorage.getItem('idToken')
    if (!idToken) {
      setGoldenExamplesLoading(false)
      return
    }
    getGoldenExamples()
      .then(({ examples }) => setGoldenExamples(examples))
      .catch(() => {})
      .finally(() => setGoldenExamplesLoading(false))
  }, [])

  const handleDeleteExample = async (exampleId: string) => {
    setDeletingExampleId(exampleId)
    try {
      await deleteGoldenExample(exampleId)
      setGoldenExamples((prev) => prev.filter((ex) => ex.id !== exampleId))
      showToast('Golden example removed')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to remove example', 'error')
    } finally {
      setDeletingExampleId(null)
    }
  }

  function handleFigmaConnect() {
    const state = crypto.randomUUID()
    sessionStorage.setItem(FIGMA_STATE_KEY, state)
    const authUrl = `https://www.figma.com/oauth?client_id=${FIGMA_CLIENT_ID}&redirect_uri=${encodeURIComponent(FIGMA_REDIRECT_URI)}&scope=${encodeURIComponent(FIGMA_SCOPES)}&state=${state}&response_type=code`
    window.location.href = authUrl
  }

  function handleJiraConnect() {
    const state = crypto.randomUUID()
    sessionStorage.setItem(JIRA_STATE_KEY, state)
    const authUrl = `https://auth.atlassian.com/authorize?audience=api.atlassian.com&client_id=${JIRA_CLIENT_ID}&scope=${encodeURIComponent(JIRA_SCOPES)}&redirect_uri=${encodeURIComponent(JIRA_REDIRECT_URI)}&state=${state}&response_type=code&prompt=consent`
    window.location.href = authUrl
  }

  async function handleFigmaDisconnect() {
    setDisconnecting(true)
    try {
      await disconnectFigma()
      setFigmaStatus({ connected: false, status: 'disconnected' })
      showToast('Figma account disconnected')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to disconnect Figma', 'error')
    } finally {
      setDisconnecting(false)
      setShowDisconnectConfirm(false)
    }
  }

  async function handleJiraDisconnect() {
    setJiraDisconnecting(true)
    try {
      await disconnectJira()
      setJiraStatus({ connected: false, status: 'disconnected' })
      showToast('Jira account disconnected')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to disconnect Jira', 'error')
    } finally {
      setJiraDisconnecting(false)
      setShowJiraDisconnectConfirm(false)
      setJiraDisconnectConnectionId(null)
    }
  }

  async function handleJiraDisconnectConnection(connectionId: string) {
    setDisconnectingConnectionId(connectionId)
    try {
      const result = await disconnectJiraConnection(connectionId)
      if (result.connections.length === 0) {
        setJiraStatus({ connected: false, status: 'disconnected' })
      } else {
        setJiraStatus(prev => prev ? { ...prev, connections: result.connections } : prev)
      }
      showToast('Jira connection removed')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to disconnect Jira connection', 'error')
    } finally {
      setDisconnectingConnectionId(null)
      setShowJiraDisconnectConfirm(false)
      setJiraDisconnectConnectionId(null)
    }
  }

  async function handleUpdateApiToken(connectionId: string) {
    if (!updateTokenValue.trim()) return
    setUpdateTokenSubmitting(true)
    try {
      const result = await updateJiraApiToken(connectionId, updateTokenValue.trim())
      setJiraStatus(prev => prev ? { ...prev, connected: true, status: 'active', connections: result.connections } : prev)
      setUpdatingTokenConnId(null)
      setUpdateTokenValue('')
      showToast('API token updated')
      loadJiraStatus()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to update token', 'error')
    } finally {
      setUpdateTokenSubmitting(false)
    }
  }

  async function handleApiTokenTest() {
    const url = apiTokenUrl.trim()
    if (!url || !apiTokenEmail.trim() || !apiTokenValue.trim()) return
    setApiTokenTesting(true)
    setApiTokenTestResult(null)
    try {
      const result = await testJiraApiToken(url, apiTokenEmail.trim(), apiTokenValue.trim())
      setApiTokenTestResult(result)
    } catch {
      setApiTokenTestResult({ success: false, error: 'Connection test failed.' })
    } finally {
      setApiTokenTesting(false)
    }
  }

  async function handleApiTokenSubmit() {
    const url = apiTokenUrl.trim()
    if (!url || !apiTokenEmail.trim() || !apiTokenValue.trim()) return
    setApiTokenSubmitting(true)
    try {
      const result = await addJiraApiToken(url, apiTokenEmail.trim(), apiTokenValue.trim())
      setJiraStatus(prev => prev ? { ...prev, connected: true, status: 'active', connections: result.connections } : prev)
      setShowApiTokenForm(false)
      setApiTokenUrl('')
      setApiTokenEmail('')
      setApiTokenValue('')
      showToast('Jira instance connected via API token')
      loadJiraStatus()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to connect', 'error')
    } finally {
      setApiTokenSubmitting(false)
    }
  }

  function formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <AppLayout title="Settings">
      <div className="max-w-[800px] mx-auto">
        {/* Appearance Section */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-linear-text mb-4">Appearance</h2>
          <div className="bg-linear-bg-card border border-linear-border-input rounded-xl p-6">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-[13px] font-medium text-linear-text">Theme</h3>

              <div
                role="radiogroup"
                aria-label="Theme preference"
                className="inline-flex p-0.5 rounded-md bg-linear-bg-input border border-linear-border-input"
              >
                {THEME_OPTIONS.map((option, index) => {
                  const selected = theme === option.value
                  // Roving tabindex per WAI radiogroup pattern: only the
                  // selected radio is in the tab order. Arrow keys move
                  // between options and call setTheme so selection
                  // follows focus, matching how native radios feel.
                  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
                    let nextIndex: number | null = null
                    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                      nextIndex = (index + 1) % THEME_OPTIONS.length
                    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                      nextIndex = (index - 1 + THEME_OPTIONS.length) % THEME_OPTIONS.length
                    } else if (event.key === ' ' || event.key === 'Enter') {
                      event.preventDefault()
                      setTheme(option.value)
                      return
                    }
                    if (nextIndex !== null) {
                      event.preventDefault()
                      const next = THEME_OPTIONS[nextIndex]
                      setTheme(next.value)
                      const button = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[nextIndex]
                      button?.focus()
                    }
                  }
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      tabIndex={selected ? 0 : -1}
                      onClick={() => setTheme(option.value)}
                      onKeyDown={handleKeyDown}
                      className={`
                        inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[12px] font-medium
                        transition-colors duration-100
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-linear-accent-focus
                        ${selected
                          ? 'bg-linear-bg-card text-linear-text shadow-sm'
                          : 'text-linear-text-muted hover:text-linear-text-secondary'
                        }
                      `}
                    >
                      {option.icon}
                      <span>{option.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>

          </div>
        </section>

        {/* Integrations Section */}
        <section>
          <h2 className="text-lg font-semibold text-linear-text mb-4">Integrations</h2>

          {/* Figma Integration Card */}
          <div className="bg-linear-bg-card border border-linear-border-input rounded-xl p-6">
            {loading ? (
              /* Loading state */
              <div className="flex items-center gap-4">
                <FigmaIcon />
                <div className="flex-1">
                  <h3 className="text-[15px] font-medium text-linear-text">Figma</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="spinner spinner-sm"></div>
                    <span className="text-linear-text-muted text-[13px]">Checking connection...</span>
                  </div>
                </div>
              </div>
            ) : figmaStatus?.status === 'active' && figmaStatus.connected ? (
              /* Connected state */
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <FigmaIcon />
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-[15px] font-medium text-linear-text">Figma</h3>
                      <span className="flex items-center gap-1.5 text-[12px] text-success">
                        <span className="w-1.5 h-1.5 rounded-full bg-success"></span>
                        Connected
                      </span>
                    </div>
                    <p className="text-linear-text-secondary text-[13px] mt-1">
                      Connected as {figmaStatus.figmaUserName}
                    </p>
                    {figmaStatus.figmaUserEmail && (
                      <p className="text-linear-text-muted text-[13px]">
                        {figmaStatus.figmaUserEmail}
                      </p>
                    )}
                    {figmaStatus.connectedAt && (
                      <p className="text-linear-text-muted text-[12px] mt-1">
                        Connected {formatDate(figmaStatus.connectedAt)}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="px-4 py-2 text-sm text-error border border-error/30 rounded-lg hover:bg-error/10 transition-colors"
                >
                  Disconnect
                </button>
              </div>
            ) : figmaStatus?.status === 'expired' ? (
              /* Expired state */
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <FigmaIcon />
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-[15px] font-medium text-linear-text">Figma</h3>
                      <span className="flex items-center gap-1.5 text-[12px] text-warning">
                        <span className="w-1.5 h-1.5 rounded-full bg-warning"></span>
                        Expired
                      </span>
                    </div>
                    <p className="text-linear-text-secondary text-[13px] mt-1">
                      Your Figma connection has expired. Reconnect to continue generating test cases from designs.
                    </p>
                  </div>
                </div>
                <button onClick={handleFigmaConnect} className="btn btn-primary flex-shrink-0">
                  Reconnect to Figma
                </button>
              </div>
            ) : (
              /* Disconnected state */
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <FigmaIcon />
                  <div>
                    <h3 className="text-[15px] font-medium text-linear-text">Figma</h3>
                    <p className="text-linear-text-secondary text-[13px] mt-1">
                      Connect your Figma account to generate test cases from designs.
                    </p>
                  </div>
                </div>
                <button onClick={handleFigmaConnect} className="btn btn-primary flex-shrink-0">
                  Connect to Figma
                </button>
              </div>
            )}
          </div>

          {/* Jira Integration Card */}
          <div className="bg-linear-bg-card border border-linear-border-input rounded-xl p-6 mt-4">
            {jiraLoading ? (
              /* Loading state */
              <div className="flex items-center gap-4">
                <JiraIcon />
                <div className="flex-1">
                  <h3 className="text-[15px] font-medium text-linear-text">Jira</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="spinner spinner-sm"></div>
                    <span className="text-linear-text-muted text-[13px]">Checking connection...</span>
                  </div>
                </div>
              </div>
            ) : jiraStatus?.status === 'active' && jiraStatus.connected ? (
              /* Connected state */
              <div>
                {/* Header row */}
                <div className="flex items-center gap-4 mb-4">
                  <JiraIcon />
                  <div className="flex items-center gap-3">
                    <h3 className="text-[15px] font-medium text-linear-text">Jira</h3>
                    <span className="flex items-center gap-1.5 text-[12px] text-success">
                      <span className="w-1.5 h-1.5 rounded-full bg-success"></span>
                      Connected
                    </span>
                  </div>
                </div>

                {/* Multi-connection UI */}
                {jiraStatus.connections && jiraStatus.connections.length > 0 ? (
                  <div className="space-y-3 ml-10">
                    {jiraStatus.connections.map((conn: JiraConnectionDetail) => (
                      <div key={conn.connectionId} className={`border rounded-lg p-4 ${conn.status === 'expired' ? 'border-warning/60' : 'border-linear-border'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-[13px] font-medium text-linear-text">
                                {conn.userName}
                              </p>
                              {conn.authType === 'apitoken' && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-linear-accent/10 text-linear-accent-focus border border-linear-accent/20">API Token</span>
                              )}
                              {conn.status === 'expired' && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning/10 text-warning border border-warning/20">Expired</span>
                              )}
                            </div>
                            <p className="text-[13px] text-linear-text-muted">
                              {conn.userEmail}
                            </p>
                            <p className="text-[12px] text-linear-text-muted mt-1">
                              Connected {formatDate(conn.connectedAt)}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setJiraDisconnectConnectionId(conn.connectionId)
                              setShowJiraDisconnectConfirm(true)
                            }}
                            className="px-3 py-1.5 text-[13px] text-error border border-error/30 rounded-lg hover:bg-error/10 transition-colors"
                          >
                            Disconnect
                          </button>
                        </div>

                        {/* Instances for this connection */}
                        {conn.instances.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-linear-border-subtle">
                            <div className="space-y-2">
                              {conn.instances.map((inst: JiraInstanceInfo) => (
                                <div key={inst.cloudId} className="flex items-center justify-between py-2 px-3 rounded-lg border border-linear-border-subtle">
                                  <div className="flex items-center gap-3">
                                    <span className={`w-2 h-2 rounded-full ${inst.active ? 'bg-success' : 'bg-linear-text-placeholder'}`}></span>
                                    <div>
                                      <p className={`text-[13px] font-medium ${inst.active ? 'text-linear-text' : 'text-linear-text-muted'}`}>
                                        {inst.cloudName}
                                      </p>
                                      <p className="text-[12px] text-linear-text-muted">
                                        {inst.cloudUrl.replace('https://', '')}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    className={`toggle ${inst.active ? 'active' : ''}`}
                                    onClick={async () => {
                                      try {
                                        const result = await toggleJiraInstance(inst.cloudId, !inst.active)
                                        setJiraStatus(prev => {
                                          if (!prev) return prev
                                          // Update instances within the matching connection
                                          const updatedConnections = prev.connections?.map(c => {
                                            if (c.connectionId === conn.connectionId) {
                                              return { ...c, instances: result.instances.filter(ri => c.instances.some(ci => ci.cloudId === ri.cloudId)) }
                                            }
                                            return c
                                          })
                                          return { ...prev, instances: result.instances, connections: updatedConnections }
                                        })
                                      } catch {
                                        showToast('Failed to update instance', 'error')
                                      }
                                    }}
                                    title={inst.active ? 'Disable this instance' : 'Enable this instance'}
                                  >
                                    <span className="toggle-knob"></span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Update Token for expired API token connections */}
                        {conn.status === 'expired' && conn.authType === 'apitoken' && (
                          <div className="mt-3 pt-3 border-t border-warning/20">
                            {updatingTokenConnId === conn.connectionId ? (
                              <div className="space-y-2">
                                <p className="text-[12px] text-warning">Paste a new API token to restore this connection.</p>
                                <input
                                  className="input"
                                  type="password"
                                  placeholder="Paste new API token"
                                  value={updateTokenValue}
                                  onChange={e => setUpdateTokenValue(e.target.value)}
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleUpdateApiToken(conn.connectionId)}
                                    disabled={updateTokenSubmitting || !updateTokenValue.trim()}
                                    className="btn btn-primary btn-sm"
                                  >
                                    {updateTokenSubmitting ? 'Updating...' : 'Update Token'}
                                  </button>
                                  <button
                                    onClick={() => { setUpdatingTokenConnId(null); setUpdateTokenValue('') }}
                                    className="btn btn-ghost btn-sm"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => setUpdatingTokenConnId(conn.connectionId)}
                                className="text-[13px] text-warning hover:opacity-80 transition-opacity"
                              >
                                Update API Token
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Connect Another Account options */}
                    <div className="flex items-center gap-4 mt-2">
                      <button
                        onClick={handleJiraConnect}
                        className="flex items-center gap-1.5 text-[13px] text-linear-accent-focus hover:text-linear-accent transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        Connect via OAuth
                      </button>
                      <span className="text-[11px] text-linear-text-placeholder">or</span>
                      <button
                        onClick={() => setShowApiTokenForm(!showApiTokenForm)}
                        className="flex items-center gap-1.5 text-[13px] text-linear-accent-focus hover:text-linear-accent transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                        </svg>
                        Connect via API Token
                      </button>
                    </div>

                    {/* API Token Form */}
                    {showApiTokenForm && (
                      <div className="mt-3 p-4 border border-linear-border rounded-lg bg-linear-bg/50">
                        <h4 className="text-[13px] font-medium text-linear-text mb-3">Connect with API Token</h4>
                        <p className="text-[11px] text-linear-text-muted mb-3">
                          Use this when the organization requires admin approval for OAuth apps. Generate a token at{' '}
                          <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="text-linear-accent-focus hover:underline">
                            id.atlassian.com
                          </a>{' '}
                          while logged into the target account.
                        </p>
                        <div className="space-y-3">
                          <div>
                            <label className="label">Instance URL</label>
                            <input
                              className="input"
                              placeholder="https://yourorg.atlassian.net"
                              value={apiTokenUrl}
                              onChange={e => setApiTokenUrl(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="label">Email</label>
                            <input
                              className="input"
                              placeholder="you@company.com"
                              value={apiTokenEmail}
                              onChange={e => setApiTokenEmail(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="label">API Token</label>
                            <input
                              className="input"
                              type="password"
                              placeholder="Paste your API token"
                              value={apiTokenValue}
                              onChange={e => setApiTokenValue(e.target.value)}
                            />
                          </div>
                          {/* Test result */}
                          {apiTokenTestResult && (
                            <div className={`p-2.5 rounded-lg text-[12px] ${apiTokenTestResult.success ? 'bg-success/5 border border-success/15 text-success' : 'bg-error/5 border border-error/15 text-error'}`}>
                              {apiTokenTestResult.success
                                ? `Connected as ${apiTokenTestResult.userName}`
                                : apiTokenTestResult.error || 'Connection failed.'}
                            </div>
                          )}

                          <div className="flex items-center gap-2 pt-1">
                            <button
                              onClick={handleApiTokenSubmit}
                              disabled={apiTokenSubmitting || !apiTokenUrl.trim() || !apiTokenEmail.trim() || !apiTokenValue.trim()}
                              className="btn btn-primary btn-sm"
                            >
                              {apiTokenSubmitting ? 'Connecting...' : 'Connect'}
                            </button>
                            <button
                              onClick={() => { setShowApiTokenForm(false); setApiTokenUrl(''); setApiTokenEmail(''); setApiTokenValue(''); setApiTokenTestResult(null) }}
                              className="btn btn-ghost btn-sm"
                            >
                              Cancel
                            </button>
                            <div className="flex-1" />
                            <button
                              onClick={handleApiTokenTest}
                              disabled={apiTokenTesting || !apiTokenUrl.trim() || !apiTokenEmail.trim() || !apiTokenValue.trim()}
                              className="btn btn-ghost btn-sm text-linear-text-muted"
                            >
                              {apiTokenTesting ? 'Testing...' : 'Test Connection'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <p className="text-[11px] text-linear-text-muted mt-2">
                      For OAuth, use an incognito window when connecting a different organization. Atlassian reuses your existing browser session.
                    </p>

                    <p className="text-[11px] text-linear-text-muted mt-1">
                      Active instances are available for sprint imports and ticket lookups across all tools.
                    </p>
                  </div>
                ) : (
                  /* Legacy single-connection fallback */
                  <div>
                    <div className="flex items-start justify-between ml-10">
                      <div>
                        <p className="text-linear-text-secondary text-[13px]">
                          Connected as {jiraStatus.jiraUserName}
                        </p>
                        {jiraStatus.jiraUserEmail && (
                          <p className="text-linear-text-muted text-[13px]">
                            {jiraStatus.jiraUserEmail}
                          </p>
                        )}
                        {(!jiraStatus.instances || jiraStatus.instances.length <= 1) && jiraStatus.jiraCloudUrl && (
                          <p className="text-linear-text-muted text-[13px]">
                            {jiraStatus.jiraCloudUrl}
                          </p>
                        )}
                        {jiraStatus.connectedAt && (
                          <p className="text-linear-text-muted text-[12px] mt-1">
                            Connected {formatDate(jiraStatus.connectedAt)}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => setShowJiraDisconnectConfirm(true)}
                        className="px-4 py-2 text-sm text-error border border-error/30 rounded-lg hover:bg-error/10 transition-colors"
                      >
                        Disconnect
                      </button>
                    </div>

                    {/* Instance List — only shown when 2+ instances */}
                    {jiraStatus.instances && jiraStatus.instances.length > 1 && (
                      <div className="mt-4 pt-4 border-t border-linear-border ml-10">
                        <h4 className="text-[13px] font-medium text-linear-text-secondary mb-3">Jira Instances</h4>
                        <div className="space-y-2">
                          {jiraStatus.instances.map((inst: JiraInstanceInfo) => (
                            <div key={inst.cloudId} className="flex items-center justify-between py-2 px-3 rounded-lg border border-linear-border-subtle">
                              <div className="flex items-center gap-3">
                                <span className={`w-2 h-2 rounded-full ${inst.active ? 'bg-success' : 'bg-linear-text-placeholder'}`}></span>
                                <div>
                                  <p className={`text-[13px] font-medium ${inst.active ? 'text-linear-text' : 'text-linear-text-muted'}`}>
                                    {inst.cloudName}
                                  </p>
                                  <p className="text-[12px] text-linear-text-muted">
                                    {inst.cloudUrl.replace('https://', '')}
                                  </p>
                                </div>
                              </div>
                              <button
                                className={`toggle ${inst.active ? 'active' : ''}`}
                                onClick={async () => {
                                  try {
                                    const result = await toggleJiraInstance(inst.cloudId, !inst.active)
                                    setJiraStatus(prev => prev ? { ...prev, instances: result.instances } : prev)
                                  } catch {
                                    showToast('Failed to update instance', 'error')
                                  }
                                }}
                                title={inst.active ? 'Disable this instance' : 'Enable this instance'}
                              >
                                <span className="toggle-knob"></span>
                              </button>
                            </div>
                          ))}
                        </div>
                        <p className="text-[11px] text-linear-text-muted mt-2">
                          Active instances are available for sprint imports and ticket lookups across all tools.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : jiraStatus?.status === 'expired' ? (
              /* Expired state */
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <JiraIcon />
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-[15px] font-medium text-linear-text">Jira</h3>
                      <span className="flex items-center gap-1.5 text-[12px] text-warning">
                        <span className="w-1.5 h-1.5 rounded-full bg-warning"></span>
                        Expired
                      </span>
                    </div>
                    <p className="text-linear-text-secondary text-[13px] mt-1">
                      Your Jira connection has expired. Reconnect to continue pulling Jira ticket details.
                    </p>
                  </div>
                </div>
                <button onClick={handleJiraConnect} className="btn btn-primary flex-shrink-0">
                  Reconnect to Jira
                </button>
              </div>
            ) : (
              /* Disconnected state */
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <JiraIcon />
                  <div>
                    <h3 className="text-[15px] font-medium text-linear-text">Jira</h3>
                    <p className="text-linear-text-secondary text-[13px] mt-1">
                      Connect your Jira account to pull ticket details when generating test cases.
                    </p>
                  </div>
                </div>
                <button onClick={handleJiraConnect} className="btn btn-primary flex-shrink-0">
                  Connect to Jira
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Golden Examples Section */}
        <section className="mt-8">
          <h2 className="text-lg font-semibold text-linear-text mb-4">Golden Examples</h2>
          <div className="bg-linear-bg-card border border-linear-border-input rounded-xl p-6">
            <p className="text-[13px] text-linear-text-muted mb-4">
              Test cases saved as style references for AI generation. These are automatically included in prompts to guide output quality and format.
            </p>
            {goldenExamplesLoading ? (
              <div className="flex items-center gap-2 py-3">
                <div className="spinner spinner-sm"></div>
                <span className="text-[13px] text-linear-text-muted">Loading examples...</span>
              </div>
            ) : goldenExamples.length === 0 ? (
              <p className="text-[13px] text-linear-text-placeholder py-2">
                No golden examples saved yet. Use the bookmark icon on generated test cases to save examples.
              </p>
            ) : (
              <div className="space-y-1.5">
                {goldenExamples.map((ex) => (
                  <div
                    key={ex.id}
                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-linear-bg border border-linear-border-subtle group"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium shrink-0 ${typeColors[ex.type] || ''}`}>
                        {typeLabels[ex.type] || ex.type}
                      </span>
                      <span className="text-[13px] text-linear-text-secondary truncate">{ex.title}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteExample(ex.id)}
                      disabled={deletingExampleId === ex.id}
                      className="text-linear-text-muted hover:text-error transition-colors shrink-0 p-1 opacity-0 group-hover:opacity-100 focus:opacity-100"
                      title="Remove golden example"
                      aria-label={`Remove golden example: ${ex.title}`}
                    >
                      {deletingExampleId === ex.id ? (
                        <div className="w-4 h-4 border-2 border-linear-text-muted/30 border-t-error rounded-full animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      )}
                    </button>
                  </div>
                ))}
                <p className="text-[11px] text-linear-text-muted mt-2 pt-1">
                  {goldenExamples.length} of 20 slots used
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Figma Disconnect Confirmation Modal */}
      {showDisconnectConfirm && (
        <div className="modal-overlay" onClick={() => setShowDisconnectConfirm(false)}>
          <div className="modal w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-[15px] font-medium text-linear-text">Disconnect Figma</h3>
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="text-linear-text-muted hover:text-linear-text transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="text-linear-text-secondary text-[13px]">
                This will remove the Figma connection from your account. You will no longer be able to generate test cases from Figma designs until you reconnect.
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleFigmaDisconnect}
                disabled={disconnecting}
                className="btn btn-danger"
              >
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Jira Disconnect Confirmation Modal */}
      {showJiraDisconnectConfirm && (
        <div className="modal-overlay" onClick={() => { setShowJiraDisconnectConfirm(false); setJiraDisconnectConnectionId(null) }}>
          <div className="modal w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-[15px] font-medium text-linear-text">Disconnect Jira</h3>
              <button
                onClick={() => { setShowJiraDisconnectConfirm(false); setJiraDisconnectConnectionId(null) }}
                className="text-linear-text-muted hover:text-linear-text transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {jiraDisconnectConnectionId ? (
                <p className="text-linear-text-secondary text-[13px]">
                  This will remove this Jira connection and its instances from your account. Other connections will not be affected.
                </p>
              ) : (
                <p className="text-linear-text-secondary text-[13px]">
                  This will remove the Jira connection from your account. You will no longer be able to pull Jira ticket details until you reconnect.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button
                onClick={() => { setShowJiraDisconnectConfirm(false); setJiraDisconnectConnectionId(null) }}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (jiraDisconnectConnectionId) {
                    handleJiraDisconnectConnection(jiraDisconnectConnectionId)
                  } else {
                    handleJiraDisconnect()
                  }
                }}
                disabled={jiraDisconnecting || disconnectingConnectionId !== null}
                className="btn btn-danger"
              >
                {(jiraDisconnecting || disconnectingConnectionId !== null) ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-4 right-4 toast toast-${toast.type} animate-fade-in z-50`}>
          {toast.type === 'success' ? (
            <svg className="w-5 h-5 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-error flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="text-linear-text">{toast.message}</span>
        </div>
      )}
    </AppLayout>
  )
}
